import { useState, useCallback } from 'react';
import { PdfJobType, CreatePdfJobData } from '@/lib/pdf-queue';
import { useLanguage } from '@/lib/i18n/LanguageContext';

interface UsePdfGenerationOptions {
  onSuccess?: (pdfUrl: string) => void;
  onError?: (error: string) => void;
  pollingInterval?: number;
  maxPollingAttempts?: number;
}

interface JobStatusResponse {
  job_id: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  pdf_url?: string;
  error_message?: string;
  created_at: string;
  completed_at?: string;
  type: PdfJobType;
}

export function usePdfGeneration(options: UsePdfGenerationOptions = {}) {
  const {
    onSuccess,
    onError,
    pollingInterval = 2000, // Poll every 2 seconds
    maxPollingAttempts = 60, // Max 2 minutes (60 * 2s)
  } = options;

  const { t } = useLanguage();
  const [isGenerating, setIsGenerating] = useState(false);
  const [progress, setProgress] = useState<string>('');
  const [error, setError] = useState<string | null>(null);

  const pollJobStatus = useCallback(
    async (jobId: string, attempt: number = 0): Promise<string> => {
      if (attempt >= maxPollingAttempts) {
        throw new Error('PDF generation timeout - job took too long');
      }

      const response = await fetch(`/api/pdf-jobs/${jobId}`);

      if (!response.ok) {
        if (response.status === 404) {
          throw new Error(t('mozSubmission.errors.jobNotFound'));
        }
        let errorMessage = t('mozSubmission.errors.failedToCheckJobStatus');
        try {
          const errorData = await response.json();
          errorMessage = errorData.error || errorMessage;
        } catch {
          // Response is not JSON (likely HTML error page)
          errorMessage = `${t('mozSubmission.errors.serverError')} (${response.status})`;
        }
        throw new Error(errorMessage);
      }

      const data: JobStatusResponse = await response.json();

      switch (data.status) {
        case 'completed':
          if (!data.pdf_url) {
            throw new Error('Job completed but no PDF URL returned');
          }
          return data.pdf_url;

        case 'failed':
          throw new Error(data.error_message || 'PDF generation failed');

        case 'processing':
          setProgress(t('mozSubmission.generatingPdf'));
          await new Promise((resolve) => setTimeout(resolve, pollingInterval));
          return pollJobStatus(jobId, attempt + 1);

        case 'pending':
        default:
          setProgress(t('mozSubmission.waitingInQueue'));
          await new Promise((resolve) => setTimeout(resolve, pollingInterval));
          return pollJobStatus(jobId, attempt + 1);
      }
    },
    [pollingInterval, maxPollingAttempts, t]
  );

  const generatePdf = useCallback(
    async (jobData: CreatePdfJobData): Promise<string | null> => {
      setIsGenerating(true);
      setProgress(t('mozSubmission.preparingPdf'));
      setError(null);

      try {
        // Create job
        const createResponse = await fetch('/api/pdf-jobs', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(jobData),
        });

        if (!createResponse.ok) {
          let errorMessage = t('mozSubmission.errors.failedToCreatePdfJob');
          try {
            const errorData = await createResponse.json();
            errorMessage = errorData.error || errorMessage;
          } catch {
            // Response is not JSON (likely HTML error page)
            errorMessage = `${t('mozSubmission.errors.serverError')} (${createResponse.status}): ${t('mozSubmission.errors.redisNotRunning')}`;
          }
          throw new Error(errorMessage);
        }

        const { job_id } = await createResponse.json();
        setProgress(t('mozSubmission.waitingInQueue'));

        // Poll for completion
        const pdfUrl = await pollJobStatus(job_id);

        setProgress(t('mozSubmission.pdfReady'));
        onSuccess?.(pdfUrl);

        return pdfUrl;
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Unknown error';
        setError(errorMessage);
        onError?.(errorMessage);
        return null;
      } finally {
        setIsGenerating(false);
        // Clear progress after a short delay
        setTimeout(() => setProgress(''), 500);
      }
    },
    [pollJobStatus, onSuccess, onError, t]
  );

  const retryJob = useCallback(
    async (jobId: string): Promise<string | null> => {
      setIsGenerating(true);
      setProgress(t('mozSubmission.retryingPdf'));
      setError(null);

      try {
        const retryResponse = await fetch(`/api/pdf-jobs/${jobId}/retry`, {
          method: 'POST',
        });

        if (!retryResponse.ok) {
          let errorMessage = t('mozSubmission.errors.failedToRetryJob');
          try {
            const errorData = await retryResponse.json();
            errorMessage = errorData.error || errorMessage;
          } catch {
            // Response is not JSON (likely HTML error page)
            errorMessage = `${t('mozSubmission.errors.serverError')} (${retryResponse.status})`;
          }
          throw new Error(errorMessage);
        }

        // Poll for completion
        const pdfUrl = await pollJobStatus(jobId);

        setProgress(t('mozSubmission.pdfReady'));
        onSuccess?.(pdfUrl);

        return pdfUrl;
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Unknown error';
        setError(errorMessage);
        onError?.(errorMessage);
        return null;
      } finally {
        setIsGenerating(false);
        setTimeout(() => setProgress(''), 500);
      }
    },
    [pollJobStatus, onSuccess, onError, t]
  );

  return {
    generatePdf,
    retryJob,
    isGenerating,
    progress,
    error,
  };
}
